import { useState, type FormEvent, type ReactNode } from 'react'
import { KeyRound, Send } from 'lucide-react'
import { Button, PasswordInput, requestPasswordReset, resetPassword } from '../../core/index.js'

function AuthCard({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-marine-lt px-4">
      <div className="w-full max-w-sm bg-white border border-marine/15 p-8 shadow-sm">
        <div className="text-[9px] font-bold uppercase tracking-[2.5px] text-coral mb-1">autonoME</div>
        {children}
      </div>
    </div>
  )
}

const INPUT = 'w-full border border-marine/25 px-3 py-2 mb-4 text-sm text-ink focus:outline-none focus:border-marine'
const LABEL = 'block text-xs font-bold text-ink-mid mb-1'

export interface ForgotPasswordProps {
  /** Where "Go to sign in" leads: the app's sign-in, or the admin area's. */
  signInPath?: string
}

/**
 * Self-service forgot password (invitations plan 3.6, D9): the person types
 * the email of their login and is sent a link and a six-digit code. The
 * answer never says whether the email has a login. The code form follows
 * at once, for someone reading the email on another device; the link in
 * the email opens the reset page by itself.
 */
export function ForgotPassword({ signInPath = '/' }: ForgotPasswordProps) {
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [stage, setStage] = useState<'ask' | 'sending' | 'code' | 'resetting' | 'done'>('ask')
  const [error, setError] = useState<string | null>(null)

  async function handleRequest(event: FormEvent): Promise<void> {
    event.preventDefault()
    setStage('sending')
    setError(null)
    try {
      await requestPasswordReset(email.trim())
      setStage('code')
    } catch (err) {
      setStage('ask')
      setError(err instanceof Error ? err.message : 'The request could not be sent')
    }
  }

  async function handleReset(event: FormEvent): Promise<void> {
    event.preventDefault()
    if (newPassword !== confirm) {
      setError('Passwords do not match')
      return
    }
    setStage('resetting')
    setError(null)
    try {
      await resetPassword({ email: email.trim(), code: code.trim() }, newPassword)
      setStage('done')
    } catch (err) {
      setStage('code')
      setError(err instanceof Error ? err.message : 'The code is wrong or has expired')
    }
  }

  if (stage === 'done') {
    return (
      <AuthCard>
        <h1 className="text-xl font-black text-ink mb-4">Password updated</h1>
        <p className="text-sm text-ink-mid mb-6">You can sign in with your new password now.</p>
        <a href={signInPath} className="text-sm font-bold text-marine hover:text-marine-dk">
          Go to sign in
        </a>
      </AuthCard>
    )
  }

  if (stage === 'ask' || stage === 'sending') {
    return (
      <AuthCard>
        <form onSubmit={handleRequest}>
          <h1 className="text-xl font-black text-ink mb-2">Forgot your password?</h1>
          <p className="text-sm text-ink-mid mb-6">Enter the email you sign in with. If it has a login, we will email a link and a six-digit code to set a new password; both work for an hour.</p>
          <label className={LABEL} htmlFor="forgot-email">
            Email
          </label>
          <input id="forgot-email" type="email" className={INPUT} value={email} onChange={(event) => setEmail(event.target.value)} autoComplete="username" autoFocus required />
          {error && <div className="text-coral text-xs font-bold mb-4">{error}</div>}
          <Button icon={Send} size="lg" fullWidth type="submit" disabled={stage === 'sending' || email.trim() === ''}>
            {stage === 'sending' ? 'Sending…' : 'Email me a reset'}
          </Button>
          <p className="text-[11px] text-ink-mid text-center mt-4">
            <a href={signInPath} className="font-bold text-marine hover:text-marine-dk">
              Back to sign in
            </a>
          </p>
        </form>
      </AuthCard>
    )
  }

  return (
    <AuthCard>
      <form onSubmit={handleReset}>
        <h1 className="text-xl font-black text-ink mb-2">Check your email</h1>
        <p className="text-sm text-ink-mid mb-6">
          If <span className="font-bold text-ink">{email.trim()}</span> has a login, a link and a code are on their way. Open the link, or enter the code here with a new password. Nothing arrived? Check the spelling, or ask whoever gave you your login for a reset link.
        </p>
        <label className={LABEL} htmlFor="forgot-code">
          Code from the email
        </label>
        <input id="forgot-code" inputMode="numeric" pattern="\d{6}" maxLength={6} className={INPUT} value={code} onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))} placeholder="123456" autoFocus required />
        <label className={LABEL} htmlFor="forgot-new-password">
          New password
        </label>
        <PasswordInput id="forgot-new-password" className={INPUT} value={newPassword} onChange={(event) => setNewPassword(event.target.value)} autoComplete="new-password" minLength={8} required />
        <label className={LABEL} htmlFor="forgot-confirm">
          Confirm new password
        </label>
        <PasswordInput id="forgot-confirm" className={INPUT} value={confirm} onChange={(event) => setConfirm(event.target.value)} autoComplete="new-password" minLength={8} required />
        {error && <div className="text-coral text-xs font-bold mb-4">{error}</div>}
        <Button icon={KeyRound} size="lg" fullWidth type="submit" disabled={stage === 'resetting' || code.length !== 6}>
          {stage === 'resetting' ? 'Saving…' : 'Set new password'}
        </Button>
        <p className="text-[11px] text-ink-mid text-center mt-4">
          <button type="button" onClick={() => setStage('ask')} className="font-bold text-marine hover:text-marine-dk">
            Send it again
          </button>
          {' · '}
          <a href={signInPath} className="font-bold text-marine hover:text-marine-dk">
            Back to sign in
          </a>
        </p>
      </form>
    </AuthCard>
  )
}
